import { AtimInterface, HitchType } from "protos/atk/enums";
import {
  atimInterfaceToLocaleString,
  hitchTypeToLocaleString,
} from "common/utils/enumTranslations";
import {
  capitalize,
  FormControl,
  FormControlLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from "@mui/material";
import { FormikTextInput } from "common/components/FormikTextInput";
import { ImplementViewModel } from "../ImplementViewModel";
import { MuiErrorMessage } from "common/components/MuiErrorMessage";
import { TemplateOverride } from "../../TemplateOverride";
import { typedPath } from "typed-path";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import React, { FC, useState } from "react";

const S = typedPath<ImplementViewModel>();

export const BasicInfoStep: FC<{ disabled?: boolean }> = ({ disabled }) => {
  const { t } = useTranslation();
  const formikProps = useFormikContext<ImplementViewModel>();

  const [checkedOverrideBox, setCheckedOverrideBox] = useState<boolean>(
    Boolean(formikProps.values.hitchType) || Boolean(formikProps.values.atim)
  );

  const hasCustomInfo =
    Boolean(formikProps.values.hitchType) ||
    Boolean(formikProps.values.atim) ||
    checkedOverrideBox;

  return (
    <div className="flex flex-col gap-2">
      <TemplateOverride
        checked={hasCustomInfo}
        disabled={disabled}
        onChange={() => {
          if (hasCustomInfo) {
            setCheckedOverrideBox(false);
            formikProps.setFieldValue(S.hitchType.$path, undefined);
            formikProps.setFieldValue(S.atim.$path, undefined);
          } else {
            setCheckedOverrideBox(true);
            formikProps.setFieldValue(
              S.hitchType.$path,
              formikProps.values.archetype?.hitchType
            );
            formikProps.setFieldValue(
              S.atim.$path,
              formikProps.values.archetype?.atim
            );
          }
        }}
      />
      <Paper className="flex flex-col p-4 gap-4">
        <FormikTextInput
          name={S.archetype.make.$path}
          label={capitalize(t("models.equipment.make_one"))}
          disabled
        />
        <FormikTextInput
          name={S.archetype.model.$path}
          label={capitalize(t("models.equipment.model_one"))}
          disabled
        />
        <FormikTextInput
          name={S.archetype.series.$path}
          label={capitalize(t("models.equipment.series"))}
          disabled
        />
        {hasCustomInfo && (
          <div className="flex flex-col">
            <Typography>
              {capitalize(t("models.equipment.hitchType"))}
            </Typography>
            <FormControl disabled={disabled}>
              <RadioGroup
                className="flex gap-2"
                row
                value={formikProps.values.hitchType}
                onChange={(e) =>
                  formikProps.setFieldValue(
                    S.hitchType.$path,
                    Number.parseInt(e.target.value)
                  )
                }
              >
                <FormControlLabel
                  value={HitchType.HITCH_TYPE_THREE_POINT_REAR}
                  control={<Radio />}
                  label={t(
                    hitchTypeToLocaleString(
                      HitchType.HITCH_TYPE_THREE_POINT_REAR
                    )
                  )}
                />
                <FormControlLabel
                  value={HitchType.HITCH_TYPE_THREE_POINT_FRONT}
                  control={<Radio />}
                  label={t(
                    hitchTypeToLocaleString(
                      HitchType.HITCH_TYPE_THREE_POINT_FRONT
                    )
                  )}
                />
                <FormControlLabel
                  value={HitchType.HITCH_TYPE_DRAW_BAR_REAR}
                  control={<Radio />}
                  label={t(
                    hitchTypeToLocaleString(HitchType.HITCH_TYPE_DRAW_BAR_REAR)
                  )}
                />
              </RadioGroup>
            </FormControl>
            <MuiErrorMessage name={S.hitchType.$path} />
          </div>
        )}
        {!hasCustomInfo && (
          <div className="flex flex-col">
            <Typography>
              {capitalize(t("models.equipment.hitchType"))}
            </Typography>
            <FormControl disabled>
              <RadioGroup
                className="flex gap-2"
                row
                value={formikProps.values.archetype?.hitchType}
              >
                <FormControlLabel
                  value={HitchType.HITCH_TYPE_THREE_POINT_REAR}
                  control={<Radio />}
                  label={t(
                    hitchTypeToLocaleString(
                      HitchType.HITCH_TYPE_THREE_POINT_REAR
                    )
                  )}
                />
                <FormControlLabel
                  value={HitchType.HITCH_TYPE_THREE_POINT_FRONT}
                  control={<Radio />}
                  label={t(
                    hitchTypeToLocaleString(
                      HitchType.HITCH_TYPE_THREE_POINT_FRONT
                    )
                  )}
                />
                <FormControlLabel
                  value={HitchType.HITCH_TYPE_DRAW_BAR_REAR}
                  control={<Radio />}
                  label={t(
                    hitchTypeToLocaleString(HitchType.HITCH_TYPE_DRAW_BAR_REAR)
                  )}
                />
              </RadioGroup>
            </FormControl>
            <MuiErrorMessage name={S.hitchType.$path} />
          </div>
        )}
        {!hasCustomInfo && (
          <div className="flex flex-col">
            <Typography>{t("models.equipment.atimInterface")}</Typography>
            <Select value={formikProps.values.archetype?.atim} disabled>
              <MenuItem value={undefined}>
                {t("utils.descriptors.none")}
              </MenuItem>
              <MenuItem value={AtimInterface.ATIM_LASER_WEEDER}>
                {t(
                  atimInterfaceToLocaleString(AtimInterface.ATIM_LASER_WEEDER)
                )}
              </MenuItem>
            </Select>
          </div>
        )}
        {hasCustomInfo && (
          <div className="flex flex-col">
            <Typography>{t("models.equipment.atimInterface")}</Typography>
            <Select
              disabled={disabled}
              value={formikProps.values.atim}
              onChange={(e) =>
                formikProps.setFieldValue(S.atim.$path, e.target.value)
              }
            >
              <MenuItem value={undefined}>
                {t("utils.descriptors.none")}
              </MenuItem>
              <MenuItem value={AtimInterface.ATIM_LASER_WEEDER}>
                {t(
                  atimInterfaceToLocaleString(AtimInterface.ATIM_LASER_WEEDER)
                )}
              </MenuItem>
            </Select>
          </div>
        )}
      </Paper>
    </div>
  );
};
